import { useHistory } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { unlockUser } from "../../services/users";

const UserAccessRow = ({ data, onUnlocked }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [unlocking, setUnlocking] = useState(false);

  const formatName = (firstName, lastName) => {
    const formattedFirstName = firstName
      ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
      : "";
    const formattedLastName = lastName ? lastName.toUpperCase() : "";
    return `${formattedLastName} ${formattedFirstName}`.trim();
  };

  const openEdit = () => {
    history.push(`/access/${data._id}`, { user: data });
  };

  const handleUnlock = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (unlocking) return;

    setUnlocking(true);
    try {
      await unlockUser(data._id);
      onUnlocked?.(data._id);
    } catch (error) {
      console.error(error);
      window.alert(t("settingsUsers.unlockError"));
    } finally {
      setUnlocking(false);
    }
  };

  const displayName = formatName(data.firstName, data.lastName) || "—";
  const addressCount =
    data.authorizedAddresses && data.authorizedAddresses.length > 0
      ? data.authorizedAddresses.length
      : 0;
  const hasMailNotifications =
    data.receiveAnomalyMail ||
    data.receiveFillingMail ||
    data.receiveRotationMail ||
    data.recieveCharacterizationMail ||
    data.recieveQualicheckMail;
  const hasSmsNotifications =
    data.receiveAnomalySms ||
    data.receiveFillingSms ||
    data.receiveRotationSms ||
    data.recieveCharacterizationSms ||
    data.recieveQualicheckSms;

  const hasPushNotifications =
    data.receiveAnomalyPush ||
    data.receiveFillingPush ||
    data.receiveRotationPush ||
    data.recieveCharacterizationPush ||
    data.recieveQualicheckPush;
  const pushAtRisk = Boolean(data.pushAtRisk);
  const isLocked = Boolean(data.isLocked);

  return (
    <div
      className="table-standard-row cursor-pointer"
      onClick={openEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openEdit();
        }
      }}
    >
      <div
        className="table-standard-row-cell min-w-0"
        data-label={t("User name")}
        title={displayName}
      >
        <span className="flex flex-col gap-1 min-w-0 w-full">
          <span className="table-standard-row-cell-truncate max-w-full">
            {displayName}
          </span>
          {isLocked || pushAtRisk || hasPushNotifications ? (
            <span className="flex flex-wrap items-center gap-2">
              {isLocked ? (
                <span
                  className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-[#fdecec] text-[#B42318]"
                  title={t("settingsUsers.lockedBadgeHint")}
                >
                  {t("settingsUsers.lockedBadge")}
                </span>
              ) : null}
              {pushAtRisk ? (
                <span
                  className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-[#fff1e8] text-[#B54708]"
                  title={t("settingsUsers.pushAtRiskBadgeHint")}
                >
                  {t("settingsUsers.pushAtRiskBadge")}
                </span>
              ) : hasPushNotifications ? (
                <span
                  className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-[#eaf7f0] text-[#1B7A4E]"
                  title={t("settingsUsers.pushOkBadgeHint")}
                >
                  {t("settingsUsers.pushOkBadge")}
                </span>
              ) : null}
              {isLocked ? (
                <button
                  type="button"
                  className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded border border-[#B42318] text-[#B42318] bg-white hover:bg-[#fdecec]"
                  onClick={handleUnlock}
                  disabled={unlocking}
                >
                  {unlocking
                    ? t("settingsUsers.unlocking")
                    : t("settingsUsers.unlockAction")}
                </button>
              ) : null}
            </span>
          ) : null}
        </span>
      </div>
      <div
        className="table-standard-row-cell text-sm text-[#5e5e5e] min-w-0"
        data-label={t("Related addresses")}
      >
        <span className="table-standard-row-cell-truncate">{addressCount}</span>
      </div>
      <div
        className="table-standard-row-cell min-w-0"
        data-label={t("Email")}
        title={data.email || ""}
      >
        <span className="flex items-center gap-2 min-w-0 w-full">
          <i
            className={`fa fa-envelope shrink-0 ${
              hasMailNotifications ? "fa-envelope-true" : "fa-envelope-false"
            }`}
            aria-hidden
          />
          <span className="table-standard-row-cell-truncate">
            {data.email || "—"}
          </span>
        </span>
      </div>
      <div
        className="table-standard-row-cell min-w-0"
        data-label={t("Phone")}
        title={data.phone || ""}
      >
        <span className="flex items-center gap-2 min-w-0 w-full">
          <i
            className={`fas fa-comment-dots shrink-0 ${
              hasSmsNotifications
                ? "fa-comment-dots-true"
                : "fa-comment-dots-false"
            }`}
            aria-hidden
          />
          <span className="table-standard-row-cell-truncate">
            {data.phone || "—"}
          </span>
        </span>
      </div>
    </div>
  );
};

export default UserAccessRow;
